import { navigateTo } from 'front-end/src/app/util/appHistory'
import { Box, Flex, Heading } from 'front-end/src/components'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import IconClock from 'front-end/src/components/common/icon/IconClock'
import IconGlobe from 'front-end/src/components/common/icon/IconGlobe'
import IconLocationPin from 'front-end/src/components/common/icon/IconLocationPin'
import IconLock from 'front-end/src/components/common/icon/IconLock'
import IconLockOpen from 'front-end/src/components/common/icon/IconLockOpen'
import IconRefresh from 'front-end/src/components/common/icon/IconRefresh'
import { getQuillStyle } from 'front-end/src/components/common/quill/EditorQuill'
import {
  TableExtended,
  DEFAULT_PAGE_LIMIT
} from 'front-end/src/components/common/table-extended/TableExtended'
import Tags from 'front-end/src/components/common/tag/Tags'
import { apolloClientPrivate } from 'front-end/src/graphql/apolloClient'
import { RecurrenceText } from 'front-end/src/pages/manage-events/components/recurrence-text/RecurrenceText'
import { DangerouslySetInnerHTML } from 'front-end/src/utils/DangerouslySetInnerHTML'
import { isUrl } from 'front-end/src/utils/url'
import React, { useContext, useState } from 'react'
import { brandColors } from 'shared/basicPalette'
import dayjs from 'shared/dayjs'
import { linkBuilder } from 'shared/linkBuilder'
import { ITag } from 'types/Tag'

import DateIcon from '../../../components/date-icon/DateIcon'
import EventStatus from '../../../components/event-status/EventStatus'
import EventRSVP from '../../../event-list/components/EventRSVP'
import { formatEventLocation } from '../../../eventUtils'
import { ManageEventContext } from '../../../ManageEventContext'
import { REMOVE_TAG_MUTATION } from '../../../operations/removeTagMutation'
import { useOccurrencesEventQuery } from './OccurrencesTab.codegen'
import IconDot from 'front-end/src/components/common/icon/IconDot'
import EventFormattedTime from 'front-end/../shared/components/event-formatted-time/EventFormattedTime'
import { Plural } from '@lingui/macro'

export const OccurrencesTab = () => {
  const {
    event: { id: eventId, title, description, is_open, recurrence, author }
  } = useContext(ManageEventContext)

  const [page, setPage] = useState(1)

  const { data, loading, refetch } = useOccurrencesEventQuery({
    fetchPolicy: 'network-only',
    variables: {
      eventId: eventId,
      limit: DEFAULT_PAGE_LIMIT,
      offset: DEFAULT_PAGE_LIMIT * (page - 1)
    }
  })
  const occurrencesData = data?.recurringEvent?.occurrences || null
  const occurrencesCount = data?.recurringEvent?.occurrencesCount || 0
  const event = data?.recurringEvent || null
  const seriesTimezone = data?.recurringEvent?.timezone || null

  const columns = React.useMemo(
    () => [
      {
        width: '72px',
        align: 'center',
        Cell: ({ row }) => (
          <DateIcon
            date={dayjs(row.original.start_time)}
            dataTest="EventDate"
          />
        ),
        accessor: 'start_time'
      },
      {
        accessor: 'title',
        Cell: ({ row }) => (
          <>
            <Flex direction="column">
              <Box fontSize="18px" fontWeight="500" data-test="EventTitle">
                {row.original.title}
              </Box>
              <Flex
                mt="4px"
                color={brandColors.grey[400]}
                fontSize={fontSizes.ssm}
                alignItems="center"
                data-test="EventTimeAndLocation"
              >
                <>
                  <IconClock mr="8px" />
                  <EventFormattedTime
                    event={row.original}
                    timezone={seriesTimezone}
                  />
                </>
                {row.original.location && (
                  <>
                    {isUrl(row.original.location) ? (
                      <IconGlobe ml="16px" mr="8px" />
                    ) : (
                      <IconLocationPin ml="16px" mr="8px" />
                    )}
                    {formatEventLocation(row.original.location)}
                  </>
                )}
              </Flex>
              <Box mt="4px">
                <Tags
                  items={row.original.tags}
                  onRemove={async ({ id: tagId }: ITag) => {
                    await apolloClientPrivate.mutate({
                      mutation: REMOVE_TAG_MUTATION,
                      variables: { eventId: row.original.id, tagId }
                    })
                    refetch()
                  }}
                  onClick={(eventTag) => {
                    navigateTo(linkBuilder.eventTag(eventTag.id))
                  }}
                />
              </Box>
            </Flex>
          </>
        )
      },
      {
        accessor: 'status',
        width: '110px',
        align: event?.published_date ? 'left' : 'right',
        Cell: ({ row }) => (
          <EventStatus alignItems="center" event={row.original} />
        )
      },
      {
        accessor: 'inviteesCount',
        width: event?.published_date ? '220px' : '0',
        align: 'center',
        Cell: ({ row }) => (
          <>
            {event?.published_date && (
              <EventRSVP alignItems="center" event={row.original} />
            )}
          </>
        )
      }
    ],
    [event?.published_date]
  )

  return (
    <>
      <Heading size="xl" as="h1" mb="4px">
        {title}
      </Heading>
      <Flex
        fontSize="12px"
        color={brandColors.grey[500]}
        data-test="EventAccessType"
      >
        {is_open ? (
          <>
            <IconLockOpen fontSize="16px" mr="6px" />
            <Box>Public</Box>
          </>
        ) : (
          <>
            <IconLock fontSize="16px" mr="6px" />
            <Box>Private</Box>
          </>
        )}
        {recurrence && (
          <>
            <IconDot m="0 4px" />
            <IconRefresh fontSize="16px" mr="6px" />
            <Box>
              <RecurrenceText recurrence={recurrence} />
            </Box>
          </>
        )}
        {author && (
          <>
            <IconDot m="0 4px" />
            <Box>Created by {author.display_name}</Box>
          </>
        )}
      </Flex>
      {description && (
        <Flex
          mt="24px"
          css={getQuillStyle('ql-editor')}
          className="ql-editor"
          color={brandColors.black}
          maxW="630px"
          data-test="EventDescription"
        >
          <DangerouslySetInnerHTML content={description as string} />
        </Flex>
      )}

      {occurrencesData && (
        <Box mt="40px">
          <TableExtended
            data={occurrencesData}
            dataCount={occurrencesCount}
            loading={loading}
            columns={columns}
            noTableHead
            onRowClick={(row) => {
              navigateTo(linkBuilder.manageEvent(row?.original.id))
            }}
            page={page}
            onPageChange={setPage}
            paginationContentBefore={
              <Box color={brandColors.grey[500]}>
                <Plural
                  value={occurrencesCount}
                  _0={null}
                  one={`# event`}
                  other={`# events`}
                />
              </Box>
            }
            dataTest="RecurringEventsTable"
          />
        </Box>
      )}
    </>
  )
}
